import { createReducer, on } from '@ngrx/store';
import { closingSetupActions } from '../actions/closing-setup.actions';
import { IAccountingFilePreview, IAccountingFileRefusal, IClosingSetup } from '../models/closing-setup.model';

export const CLOSING_SETUP_FEATURE_KEY = 'closingSetup';

/** What a request is doing, so the screen can tell "no setup" from "not loaded yet". */
export type ClosingSetupStatus = 'idle' | 'loading' | 'loaded' | 'failed';

/**
 * REV-22007 — where the uploaded trial balance stands. The same vocabulary as the wizard's
 * `ClosingWizardFileState`, deliberately: the page maps nothing, it passes the value through.
 */
export type AccountingFileStatus = 'idle' | 'uploading' | 'ready' | 'refused';

export interface ClosingSetupState {
    /**
     * The closing this state belongs to.
     *
     * Load-bearing, not informational: a late answer for another closing is dropped against it, and
     * switching closings clears rather than showing the previous one's launch.
     */
    readonly projectId: string | null;
    /** `null` means the wizard has never run for this closing — the "open the wizard" case. */
    readonly setup: IClosingSetup | null;
    readonly status: ClosingSetupStatus;
    readonly error: string | null;

    /**
     * Why the START request itself was refused — kept apart from `error` on purpose.
     *
     * ⚠️ Both used to share `error`, and the launch screen's escape reacted to any of it. The poll
     * writes there every 2s for as long as the launch runs, so a single failed tick tore the launch screen down
     * mid-run, opened the wizard and showed Angular's technical text — while the pipeline carried on
     * invisibly (Code-Review Runde 3, R3-1). A refused start is a different event from a poll that
     * blipped, and only the first one may close the screen.
     */
    readonly startError: string | null;
    /** True between pressing "Abschlussprozess starten" and the server acknowledging it. */
    readonly starting: boolean;
    /** Whether the launch screen's poll is running. */
    readonly polling: boolean;

    // REV-22007 — the accounting file (ADR-0020). Belongs to the same closing as everything above,
    // and is cleared with it when the closing changes: a file uploaded for one mandate must never be
    // offered as "ready" on another.
    readonly accountingFileStatus: AccountingFileStatus;
    /** Non-null exactly while `accountingFileStatus` is `ready`. */
    readonly accountingFilePreview: IAccountingFilePreview | null;
    /** Non-null exactly while `accountingFileStatus` is `refused`. */
    readonly accountingFileRefusal: IAccountingFileRefusal | null;
}

export const initialClosingSetupState: ClosingSetupState = {
    projectId: null,
    setup: null,
    status: 'idle',
    error: null,
    startError: null,
    starting: false,
    polling: false,
    accountingFileStatus: 'idle',
    accountingFilePreview: null,
    accountingFileRefusal: null,
};

/** A late answer for a closing the user has already left. */
function isStale(state: ClosingSetupState, projectId: string): boolean {
    return state.projectId !== null && state.projectId !== projectId;
}

export const closingSetupReducer = createReducer(
    initialClosingSetupState,
    on(closingSetupActions.loadSetup, (state, { projectId }) =>
        // Switching closing CLEARS rather than keeping the old setup under a spinner: the navigation
        // reads `started` off this state, and a stale `true` would send the user past the wizard of a
        // closing that has not started.
        state.projectId === projectId
            ? { ...state, status: 'loading' as const, error: null }
            : { ...initialClosingSetupState, projectId, status: 'loading' as const },
    ),
    on(closingSetupActions.loadSetupSuccess, (state, { projectId, setup }) =>
        isStale(state, projectId) ? state : { ...state, projectId, setup, status: 'loaded' as const, error: null },
    ),
    on(closingSetupActions.loadSetupFailure, (state, { projectId, error }) =>
        isStale(state, projectId) ? state : { ...state, status: 'failed' as const, error },
    ),
    // The poll replaces the record and nothing else: `status` stays what the gate's read said. A
    // failed tick changes nothing at all, the next one asks again.
    on(closingSetupActions.refreshSetupSuccess, (state, { projectId, setup }) =>
        isStale(state, projectId) ? state : { ...state, setup },
    ),

    on(closingSetupActions.startClosing, (state, { projectId }) => ({
        ...state,
        projectId,
        starting: true,
        error: null,
    })),
    on(closingSetupActions.startClosingSuccess, (state, { projectId, setup }) =>
        isStale(state, projectId)
            ? state
            : // `polling: true` is set HERE and not by the poll effect, so the launch screen renders
              // its running state on the same tick the wizard closes — not one poll interval later.
              {
                  ...state,
                  projectId,
                  setup,
                  status: 'loaded' as const,
                  starting: false,
                  polling: true,
                  error: null,
                  startError: null,
              },
    ),
    on(closingSetupActions.startClosingFailure, (state, { projectId, error }) =>
        isStale(state, projectId) ? state : { ...state, starting: false, error, startError: error },
    ),

    on(closingSetupActions.stopPolling, (state, { projectId }) =>
        isStale(state, projectId) ? state : { ...state, polling: false },
    ),
    on(closingSetupActions.watchLaunch, (state, { projectId }) =>
        isStale(state, projectId) ? state : { ...state, polling: true },
    ),

    // ── REV-22007 — the accounting file ──────────────────────────────────────────────────────────
    // An upload for ANOTHER closing than the one in state starts over rather than mixing: the slice
    // belongs to exactly one closing (see `projectId`), and a file is part of that closing's answers.
    on(closingSetupActions.uploadAccountingFile, (state, { projectId }) => ({
        ...(isStale(state, projectId) ? { ...initialClosingSetupState, projectId } : state),
        projectId,
        accountingFileStatus: 'uploading' as const,
        accountingFilePreview: null,
        accountingFileRefusal: null,
    })),
    on(closingSetupActions.uploadAccountingFileSuccess, (state, { projectId, preview }) =>
        isStale(state, projectId)
            ? state
            : {
                  ...state,
                  accountingFileStatus: 'ready' as const,
                  accountingFilePreview: preview,
                  accountingFileRefusal: null,
              },
    ),
    on(closingSetupActions.uploadAccountingFileFailure, (state, { projectId, refusal }) =>
        isStale(state, projectId)
            ? state
            : {
                  ...state,
                  accountingFileStatus: 'refused' as const,
                  accountingFilePreview: null,
                  accountingFileRefusal: refusal,
              },
    ),
    on(closingSetupActions.resetAccountingFile, (state, { projectId }) =>
        isStale(state, projectId)
            ? state
            : {
                  ...state,
                  accountingFileStatus: 'idle' as const,
                  accountingFilePreview: null,
                  accountingFileRefusal: null,
              },
    ),
);
